// ============================================================================
// AdminTracksPanel — track plans + milestones (Build S5, Task #22).
// ============================================================================
// Sidebar: #admin/tracks (Tracks & Milestones group). LIST (filters) + DETAIL
// (route-driven). Detail: the track's 4 stage-milestones with status transitions
// (§15.3 — API promotes the track on first in_progress; server enforces) and
// evidence display (flags milestones whose completion still needs evidence; the
// attach action is API-only, not yet surfaced here). Amber-flags track plans
// without a linked plan document.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  Loader2, AlertCircle, LayoutGrid, ArrowLeft, CheckCircle2, XCircle,
  MinusCircle, ArrowRightCircle, Paperclip,
} from 'lucide-react';
import {
  api, type TrackPlanSummary, type AdminTrackDetail, type MilestoneStatus,
  type TrackPlanStatus, type FarmMilestone,
} from '../api';

interface Props {
  token: string;
  trackPlanId?: string;  // route param carries the track_plan id (per-track detail)
}

const TRACK_LABELS: Record<string, string> = {
  CONS: 'Conservation', PROD: 'Production', FSAF: 'Food Safety', MKTG: 'Sales & Marketing',
};
const TRACK_TONES: Record<string, string> = {
  available: 'bg-gray-100 text-gray-700', active: 'bg-green-100 text-green-800',
  on_hold: 'bg-amber-100 text-amber-800', withdrawn: 'bg-gray-200 text-gray-600',
  completed: 'bg-green-200 text-green-900',
};
const MILESTONE_TONES: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-700', in_progress: 'bg-blue-100 text-blue-800',
  submitted: 'bg-blue-100 text-blue-800', under_review: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800', not_applicable: 'bg-gray-100 text-gray-500',
  waived: 'bg-purple-100 text-purple-800',
};

function Pill({ status, tones }: { status: string; tones: Record<string, string> }) {
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${tones[status] ?? 'bg-gray-100 text-gray-700'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export function AdminTracksPanel({ token, trackPlanId }: Props) {
  if (trackPlanId !== undefined) return <TrackDetail token={token} trackPlanId={trackPlanId} />;
  return <TrackList token={token} />;
}

// ============================================================================
// LIST
// ============================================================================
function TrackList({ token }: { token: string }) {
  const [rows, setRows] = useState<TrackPlanSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.adminTracks(token, statusFilter ? { status: statusFilter } : undefined);
      setRows(res.track_plans);
    } catch (err: any) {
      setError(err?.message ?? 'Could not load track plans');
    }
  }, [token, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  if (error) {
    return <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-4"><AlertCircle className="w-5 h-5 shrink-0" /> {error}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <LayoutGrid className="w-6 h-6 text-green-700" />
          <h2 className="text-xl font-bold text-gray-900">Track plans</h2>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
          aria-label="Filter by status"
        >
          <option value="">All statuses</option>
          {Object.keys(TRACK_TONES).map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
      </div>

      {!rows ? (
        <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white rounded-xl border border-gray-200 p-6">No track plans match.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-100">
                <th className="p-3">Farm</th>
                <th className="p-3">Track</th>
                <th className="p-3">Status</th>
                <th className="p-3">Milestones</th>
                <th className="p-3">Plan doc</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.track_plan_id}
                  className="border-b border-gray-50 hover:bg-green-50/40 focus:bg-green-50 cursor-pointer"
                  onClick={() => { window.location.hash = `#admin/tracks/${t.track_plan_id}`; }}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open ${TRACK_LABELS[t.track_code] ?? t.track_code} track for ${t.farm_name}`}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter' || ev.key === ' ') {
                      ev.preventDefault();
                      window.location.hash = `#admin/tracks/${t.track_plan_id}`;
                    }
                  }}
                >
                  <td className="p-3">
                    <span className="font-mono text-xs text-gray-500 mr-2">{t.farm_code}</span>
                    <span className="font-medium text-gray-900">{t.farm_name}</span>
                  </td>
                  <td className="p-3 text-gray-700">{TRACK_LABELS[t.track_code] ?? t.track_code}</td>
                  <td className="p-3"><Pill status={t.status} tones={TRACK_TONES} /></td>
                  <td className="p-3 text-gray-600">{t.milestone_completed}/{t.milestone_total}</td>
                  <td className="p-3">
                    {t.plan_document_id
                      ? <span className="inline-flex items-center gap-1 text-green-700 text-xs font-semibold"><CheckCircle2 className="w-4 h-4" /> linked</span>
                      : <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-semibold"><MinusCircle className="w-4 h-4" /> none</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DETAIL
// ============================================================================
function TrackDetail({ token, trackPlanId }: { token: string; trackPlanId: string }) {
  const [detail, setDetail] = useState<AdminTrackDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [trackTo, setTrackTo] = useState<TrackPlanStatus | ''>('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.adminTrackDetail(token, trackPlanId);
      setDetail(res);
    } catch (err: any) {
      setError(err?.message ?? 'Could not load track plan');
    }
  }, [token, trackPlanId]);

  useEffect(() => { void load(); }, [load]);

  const doTrackTransition = async () => {
    if (!trackTo || busy) return;
    setBusy(true); setError(null);
    try {
      await api.adminTrackPatch(token, trackPlanId, { status: trackTo });
      setTrackTo('');
      await load();
    } catch (err: any) {
      setError(err?.message ?? 'Transition failed');
    } finally {
      setBusy(false);
    }
  };

  if (!detail && !error) {
    return <div className="flex items-center gap-2 text-gray-500 p-6"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={() => { window.location.hash = '#admin/tracks'; }}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="w-4 h-4" /> All track plans
      </button>

      {error && (
        <div className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg p-3 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {detail && (
        <>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  <span className="font-mono text-sm text-gray-500 mr-2">{detail.track_plan.farm_code}</span>
                  {detail.track_plan.farm_name}
                </h2>
                <div className="mt-1 text-sm text-gray-600">
                  {TRACK_LABELS[detail.track_plan.track_code] ?? detail.track_plan.track_code} track · {detail.track_plan.year_no}
                </div>
              </div>
              <Pill status={detail.track_plan.status} tones={TRACK_TONES} />
            </div>
            {detail.allowed_transitions.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap items-end gap-2">
                <label className="block text-sm">
                  <span className="text-gray-700">Track status</span>
                  <select
                    value={trackTo}
                    onChange={(e) => setTrackTo(e.target.value as TrackPlanStatus | '')}
                    className="mt-1 border border-gray-300 rounded-lg px-3 py-1.5 bg-white block"
                  >
                    <option value="">Choose…</option>
                    {detail.allowed_transitions.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                  </select>
                </label>
                <button
                  type="button"
                  disabled={!trackTo || busy}
                  onClick={() => void doTrackTransition()}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-50"
                >
                  <ArrowRightCircle className="w-4 h-4" /> Apply
                </button>
                <p className="w-full mt-1 text-xs text-gray-500">
                  A track promotes to <b>active</b> automatically when its first milestone begins — the server stamps it.
                </p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <h3 className="font-semibold text-gray-900">Milestones</h3>
            <div className="mt-3 space-y-3">
              {detail.milestones.map((m) => (
                <MilestoneRow key={m.farm_milestone_id} token={token} milestone={m} onChange={load} setError={setError} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MilestoneRow({
  token, milestone, onChange, setError,
}: {
  token: string;
  milestone: FarmMilestone;
  onChange: () => Promise<void>;
  setError: (s: string | null) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState<MilestoneStatus | ''>('');
  const [reason, setReason] = useState('');
  const allowed = milestone.allowed_transitions ?? [];

  const apply = async () => {
    if (!to || busy) return;
    setBusy(true); setError(null);
    try {
      const input: { to_status: MilestoneStatus; reason?: string | null; not_applicable_reason?: string | null; waived_reason?: string | null } = { to_status: to };
      if (to === 'waived') input.waived_reason = reason || null;
      else if (to === 'not_applicable') input.not_applicable_reason = reason || null;
      else input.reason = reason || null;
      await api.adminMilestoneTransition(token, milestone.farm_milestone_id, input);
      setTo(''); setReason('');
      await onChange();
    } catch (err: any) {
      setError(err?.message ?? 'Milestone transition failed');
    } finally {
      setBusy(false);
    }
  };

  const needsReason = to === 'waived' || to === 'not_applicable';

  return (
    <div className="border border-gray-100 rounded-lg p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-gray-400">{milestone.stage_code.replace(/_/g, ' ')}</span>
          <span className="text-sm font-medium text-gray-800">{milestone.label}</span>
          {milestone.def_is_draft && <span className="text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">draft</span>}
        </div>
        <Pill status={milestone.status} tones={MILESTONE_TONES} />
      </div>

      {milestone.evidence.length > 0 && (
        <ul className="mt-2 space-y-1">
          {milestone.evidence.map((ev) => (
            <li key={ev.evidence_id} className="text-xs text-gray-500 flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> {ev.evidence_type}
              {ev.document_id && <a href={`#admin/documents/${ev.document_id}`} className="text-green-700 underline">document</a>}
              {ev.notes ? ` — ${ev.notes}` : ''}
            </li>
          ))}
        </ul>
      )}

      {allowed.length > 0 && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <select value={to} onChange={(e) => setTo(e.target.value as MilestoneStatus | '')} className="border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white">
            <option value="">Transition…</option>
            {allowed.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={needsReason ? 'reason (required)' : 'note (optional)'}
            className="border border-gray-300 rounded-lg px-2 py-1 text-sm grow max-w-xs"
          />
          <button
            type="button"
            disabled={!to || busy || (needsReason && !reason)}
            onClick={() => void apply()}
            className="inline-flex items-center gap-1 px-3 py-1 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-50"
          >
            <ArrowRightCircle className="w-3.5 h-3.5" /> Apply
          </button>
          {to === 'completed' && (milestone.evidence_required?.length ?? 0) > 0 && milestone.evidence.length === 0 && (
            <span className="w-full text-xs text-amber-700 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> completion needs evidence: {milestone.evidence_required?.join(', ')}</span>
          )}
        </div>
      )}
    </div>
  );
}
